import { FetchError } from './fetch-error';
import type {
  ApiError,
  Interceptor,
  Interceptors,
  RequestConfig,
  Response,
} from './types';

export class FetchClient {
  private baseURL: string;
  private defaultConfig: RequestConfig;
  private interceptors: Interceptors;

  constructor(config: RequestConfig = {}) {
    this.baseURL = config.baseURL || '';
    this.defaultConfig = config;
    this.interceptors = {
      request: [],
      response: [],
      error: [],
    };
  }

  // Interceptor management
  useRequestInterceptor(interceptor: Interceptor<RequestConfig>) {
    this.interceptors.request.push(interceptor);
    return () => {
      const index = this.interceptors.request.indexOf(interceptor);
      if (index > -1) this.interceptors.request.splice(index, 1);
    };
  }

  useResponseInterceptor(interceptor: Interceptor<Response>) {
    this.interceptors.response.push(interceptor);
    return () => {
      const index = this.interceptors.response.indexOf(interceptor);
      if (index > -1) this.interceptors.response.splice(index, 1);
    };
  }

  useErrorInterceptor(interceptor: (error: unknown) => unknown) {
    this.interceptors.error.push(interceptor);
    return () => {
      const index = this.interceptors.error.indexOf(interceptor);
      if (index > -1) this.interceptors.error.splice(index, 1);
    };
  }

  // response parser
  private async parseResponse<T = unknown>(
    response: globalThis.Response,
  ): Promise<T> {
    let data: T;
    const contentType = response.headers.get('content-type');

    if (contentType?.includes('application/json')) {
      data = await response.json();
    } else if (contentType?.includes('text')) {
      data = (await response.text()) as T;
    } else {
      data = (await response.blob()) as T;
    }

    return data;
  }

  // Build URL with query params
  private buildURL(url: string, params?: Record<string, unknown>): string {
    const fullURL = url.startsWith('http') ? url : `${this.baseURL}${url}`;

    if (!params) return fullURL;

    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });

    const queryString = searchParams.toString();
    return queryString ? `${fullURL}?${queryString}` : fullURL;
  }

  // Apply request interceptors
  private async applyRequestInterceptors(
    config: RequestConfig,
  ): Promise<RequestConfig> {
    let processedConfig = { ...config };

    for (const interceptor of this.interceptors.request) {
      processedConfig = await interceptor(processedConfig);
    }

    return processedConfig;
  }

  // Apply response interceptors
  private async applyResponseInterceptors(
    response: Response,
  ): Promise<Response> {
    let processedResponse = response;

    for (const interceptor of this.interceptors.response) {
      processedResponse = await interceptor(processedResponse);
    }

    return processedResponse;
  }

  // Apply error interceptors
  private async applyErrorInterceptors(error: unknown): Promise<unknown> {
    let processedError = error;

    for (const interceptor of this.interceptors.error) {
      processedError = await interceptor(processedError);
    }

    return processedError;
  }

  // Core request method
  async request<T = unknown>(config: RequestConfig): Promise<Response<T>> {
    try {
      // Merge configs
      const mergedConfig: RequestConfig = {
        ...this.defaultConfig,
        ...config,
        credentials: 'include',
      };

      // Apply request interceptors
      const processedConfig = await this.applyRequestInterceptors(mergedConfig);

      // Build URL
      const url = this.buildURL(
        processedConfig.url || '',
        processedConfig.params,
      );

      // Handle timeout
      const controller = new AbortController();
      const timeoutId = processedConfig.timeout
        ? setTimeout(() => controller.abort(), processedConfig.timeout)
        : null;

      // Make request
      const fetchResponse = await fetch(url, {
        ...processedConfig,
        signal: controller.signal,
      });

      if (timeoutId) clearTimeout(timeoutId);

      // Check for HTTP errors
      if (!fetchResponse.ok) {
        const errorData = (await this.parseResponse(
          fetchResponse,
        )) as ApiError<T>;

        throw new FetchError(
          `Request failed with status ${fetchResponse.status}`,
          fetchResponse.status,
          errorData.error,
          processedConfig,
        );
      }

      // Build response object
      const data = await this.parseResponse<T>(fetchResponse);
      const response: Response<T> = {
        data,
        status: fetchResponse.status,
        statusText: fetchResponse.statusText,
        headers: fetchResponse.headers,
        config: processedConfig,
      };

      // Apply response interceptors
      return (await this.applyResponseInterceptors(response)) as Response<T>;
    } catch (error: unknown) {
      // Handle timeout
      if (error instanceof Error && error.name === 'AbortError') {
        const timeoutError = new FetchError(
          'Request timeout',
          408,
          null,
          config,
        );
        throw await this.applyErrorInterceptors(timeoutError);
      }

      // Apply error interceptors
      throw await this.applyErrorInterceptors(error);
    }
  }

  // Convenience methods
  get<T = unknown>(url: string, config?: RequestConfig): Promise<Response<T>> {
    return this.request<T>({ ...config, url, method: 'GET' });
  }

  post<T = unknown>(
    url: string,
    data?: unknown,
    config?: RequestConfig,
  ): Promise<Response<T>> {
    return this.request<T>({
      ...config,
      url,
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  put<T = unknown>(
    url: string,
    data?: unknown,
    config?: RequestConfig,
  ): Promise<Response<T>> {
    return this.request<T>({
      ...config,
      url,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: RequestConfig,
  ): Promise<Response<T>> {
    return this.request<T>({
      ...config,
      url,
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  delete<T = unknown>(
    url: string,
    config?: RequestConfig,
  ): Promise<Response<T>> {
    return this.request<T>({ ...config, url, method: 'DELETE' });
  }
}

// Factory function
export const createClient = (config?: RequestConfig) => new FetchClient(config);

// Default instance
export const client = createClient();
